import type { EffectSoundConfig } from '@shared/lib/effect-registry'

/**
 * Браузеры блокируют автозапуск звука: AudioContext создаётся в состоянии
 * "suspended" и не может играть, пока на странице не произойдёт пользовательский
 * жест (клик/тап/клавиша). Ошибка:
 *   "The AudioContext was not allowed to start. It must be resumed (or created)
 *    after a user gesture on the page."
 *
 * Решение:
 *  - getContext() навешивает одноразовые слушатели жестов, которые вызывают
 *    resume() — контекст разблокируется при первом взаимодействии;
 *  - в OBS Browser Source жеста нет, но autoplay там обычно разрешён, поэтому
 *    resume() в play() срабатывает сразу;
 *  - если на момент play() контекст ещё не разблокирован, звук кладётся в
 *    очередь pendingPlays и проигрывается автоматически сразу после resume()
 *    (вместо тихого пропуска).
 */
class AudioService {
  private ctx: AudioContext | null = null
  private bufferCache = new Map<string, AudioBuffer>()
  private masterGain: GainNode | null = null
  /** Звуки, которые не удалось проиграть из-за заблокированного контекста. */
  private pendingPlays: EffectSoundConfig[] = []
  private gestureListenersAttached = false

  private getContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext()
      this.masterGain = this.ctx.createGain()
      this.masterGain.connect(this.ctx.destination)
      this.attachGestureUnlock()
    }
    return this.ctx
  }

  /**
   * Навешивает одноразовые слушатели пользовательских жестов, которые
   * разблокируют (resume) AudioContext. Нужно для обычного браузера, где
   * autoplay-политика требует жеста. Идемпотентно.
   */
  private attachGestureUnlock(): void {
    if (this.gestureListenersAttached) return
    if (typeof window === 'undefined') return
    this.gestureListenersAttached = true

    const events: Array<keyof WindowEventMap> = [
      'pointerdown',
      'mousedown',
      'touchstart',
      'keydown',
      'click',
    ]
    const handler = () => {
      void this.unlock()
      for (const ev of events) window.removeEventListener(ev, handler)
    }
    for (const ev of events) {
      window.addEventListener(ev, handler, { once: false, passive: true })
    }
  }

  /**
   * Явно разблокирует звук. Можно вызывать из любого обработчика жеста.
   * После успешного resume проигрывает накопившиеся отложенные звуки.
   */
  async unlock(): Promise<void> {
    try {
      const ctx = this.getContext()
      if (ctx.state === 'suspended') await ctx.resume()
      if (ctx.state === 'running') this.flushPending()
    } catch {
      // Контекст ещё нельзя разблокировать — попробуем при следующем жесте
    }
  }

  private flushPending(): void {
    if (this.pendingPlays.length === 0) return
    const queued = this.pendingPlays
    this.pendingPlays = []
    for (const config of queued) void this.play(config)
  }

  async preload(src: string): Promise<void> {
    if (this.bufferCache.has(src)) return
    try {
      const ctx = this.getContext()
      const arrayBuffer = await this.fetchAudioBytes(src)
      if (!arrayBuffer) return
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer)
      this.bufferCache.set(src, audioBuffer)
    } catch {
      // Молча пропускаем отсутствующий/битый звук — overlay не должен ломаться без звуков
    }
  }

  /**
   * Достаёт байты аудио из src.
   *
   * Для data-URL декодируем base64 НАПРЯМУЮ, а не через fetch():
   *  - Chromium (в т.ч. OBS Browser Source) отвергает невалидные MIME data-URL
   *    (напр. `data:audio/mp3;base64,...`) ошибкой ERR_INVALID_URL ещё до сети;
   *  - decodeAudioData по байтам не зависит от заявленного MIME-типа.
   */
  private async fetchAudioBytes(src: string): Promise<ArrayBuffer | null> {
    if (src.startsWith('data:')) {
      return this.decodeDataUrl(src)
    }
    const response = await fetch(src)
    if (!response.ok) return null
    return response.arrayBuffer()
  }

  /** Парсит data-URL и возвращает сырые байты (поддержка base64 и percent-encoded). */
  private decodeDataUrl(dataUrl: string): ArrayBuffer | null {
    const comma = dataUrl.indexOf(',')
    if (comma === -1) return null

    const meta = dataUrl.slice(5, comma) // без префикса 'data:'
    const payload = dataUrl.slice(comma + 1)
    if (!payload) return null // пустой data-URL → нет звука

    const isBase64 = /;base64/i.test(meta)
    try {
      if (isBase64) {
        const binary = atob(this.normalizeBase64(payload))
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
        return bytes.buffer
      }
      // не-base64 data-URL (percent-encoded текст) — редко для аудио, но поддержим
      const decoded = decodeURIComponent(payload)
      const bytes = new Uint8Array(decoded.length)
      for (let i = 0; i < decoded.length; i++) bytes[i] = decoded.charCodeAt(i)
      return bytes.buffer
    } catch {
      return null
    }
  }

  /**
   * Нормализует base64-payload, чтобы atob не падал на «грязных» данных:
   *  - убирает пробелы/переводы строк и URL-safe символы приводит к стандартным;
   *  - отбрасывает всё, что вне base64-алфавита;
   *  - длина base64 ОБЯЗАНА давать остаток 0/2/3 по модулю 4. Остаток 1 —
   *    битые данные (один лишний/недостающий символ): такой хвост браузерный
   *    atob либо отвергает (InvalidCharacterError), либо декодирует со сдвигом,
   *    из-за чего decodeAudioData падает с "EncodingError: Unable to decode
   *    audio data". Обрезаем недопустимый хвост и добавляем корректный padding.
   */
  private normalizeBase64(input: string): string {
    let s = input
      .replace(/\s+/g, '')
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .replace(/[^A-Za-z0-9+/=]/g, '')
      .replace(/=+$/g, '') // снимаем существующий padding, добавим заново

    const remainder = s.length % 4
    if (remainder === 1) {
      // невалидная длина — отбрасываем лишний хвостовой символ
      s = s.slice(0, s.length - 1)
    } else if (remainder === 2) {
      s += '=='
    } else if (remainder === 3) {
      s += '='
    }
    return s
  }

  async play(config: EffectSoundConfig): Promise<void> {
    const { src, volume = 1.0, delayMs = 0 } = config
    try {
      const ctx = this.getContext()

      // Пытаемся разблокировать. Если жеста ещё не было — откладываем звук,
      // он проиграется автоматически после первого взаимодействия (flushPending).
      if (ctx.state === 'suspended') await ctx.resume()
      if (ctx.state !== 'running') {
        this.pendingPlays.push(config)
        return
      }

      if (!this.bufferCache.has(src)) await this.preload(src)

      const buffer = this.bufferCache.get(src)
      if (!buffer) return

      const source = ctx.createBufferSource()
      source.buffer = buffer

      const gainNode = ctx.createGain()
      gainNode.gain.setValueAtTime(Math.max(0, Math.min(1, volume)), ctx.currentTime)
      source.connect(gainNode)
      gainNode.connect(this.masterGain ?? ctx.destination)

      const startTime = ctx.currentTime + delayMs / 1000
      source.start(startTime)
    } catch {
      // Молча пропускаем ошибки воспроизведения
    }
  }

  setMasterVolume(volume: number): void {
    if (this.masterGain) {
      this.masterGain.gain.setValueAtTime(
        Math.max(0, Math.min(1, volume)),
        this.getContext().currentTime
      )
    }
  }

  dispose(): void {
    void this.ctx?.close()
    this.ctx = null
    this.masterGain = null
    this.bufferCache.clear()
    this.pendingPlays = []
    this.gestureListenersAttached = false
  }
}

export const audioService = new AudioService()
